import React, { useState, useRef } from "react";
import { api } from "@/lib/http";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger } from "@/components/ui/dialog";
import { Upload, FileSpreadsheet, Download, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";

// minimal CSV parser (handles quoted fields, commas, escaped quotes, CRLF)
function parseCSV(text) {
  const rows = [];
  let row = [], cell = "", q = false;
  text = text.replace(/\r\n/g, "\n").replace(/\r/g, "\n");
  for (let i = 0; i < text.length; i++) {
    const c = text[i];
    if (q) {
      if (c === '"') { if (text[i + 1] === '"') { cell += '"'; i++; } else q = false; }
      else cell += c;
    } else {
      if (c === '"') q = true;
      else if (c === ",") { row.push(cell); cell = ""; }
      else if (c === "\n") { row.push(cell); rows.push(row); row = []; cell = ""; }
      else cell += c;
    }
  }
  if (cell.length || row.length) { row.push(cell); rows.push(row); }
  return rows.filter((r) => r.some((c) => (c || "").trim() !== ""));
}

const norm = (s) => (s || "").toLowerCase().replace(/[^a-z]/g, "");
const MAP = {
  name: ["name", "fullname", "clientname", "customer", "customername"],
  first: ["firstname", "first", "givenname"],
  last: ["lastname", "last", "surname", "familyname"],
  email: ["email", "emailaddress", "e-mail"],
  phone: ["phone", "mobile", "phonenumber", "mobilenumber", "cell", "contact", "telephone"],
  dob: ["dob", "dateofbirth", "birthday", "birthdate"],
  source: ["source", "referral", "referredby", "howheard", "leadsource"],
  notes: ["notes", "note", "comments", "comment"],
  marketing_opt_in: ["marketing", "optin", "marketingoptin", "subscribed", "emailmarketing", "consent"],
  is_legacy: ["legacy", "vip", "islegacy", "legacyclient"],
};
const truthy = (v) => ["yes", "y", "true", "1", "on", "subscribed", "opted in"].includes((v || "").trim().toLowerCase());

function mapRows(rows) {
  if (rows.length < 2) return [];
  const headers = rows[0].map(norm);
  const idx = {};
  for (const [key, aliases] of Object.entries(MAP)) {
    const at = headers.findIndex((h) => aliases.includes(h));
    if (at >= 0) idx[key] = at;
  }
  const get = (r, k) => (idx[k] != null ? (r[idx[k]] || "").trim() : "");
  return rows.slice(1).map((r) => {
    let name = get(r, "name");
    if (!name) name = [get(r, "first"), get(r, "last")].filter(Boolean).join(" ").trim();
    return { name, email: get(r, "email"), phone: get(r, "phone"), dob: get(r, "dob"),
      source: get(r, "source"), notes: get(r, "notes"),
      marketing_opt_in: truthy(get(r, "marketing_opt_in")), is_legacy: truthy(get(r, "is_legacy")) };
  }).filter((c) => c.name);
}

const TEMPLATE = "name,email,phone,dob,source,notes,marketing_opt_in,legacy\nJane Smith,jane@example.com,0400 000 000,1990-05-14,Instagram,Prefers afternoons,yes,no\n";

const BOOKING_MAP = {
  client_name: ["clientname", "name", "customer", "customername", "client"],
  client_phone: ["phone", "mobile", "clientphone", "phonenumber"],
  client_email: ["email", "clientemail", "emailaddress"],
  service: ["service", "servicename", "treatment"],
  staff: ["staff", "staffname", "practitioner", "therapist", "provider", "with"],
  date: ["date", "appointmentdate", "day"],
  time: ["time", "starttime", "appointmenttime", "start"],
  status: ["status", "state"],
  price: ["price", "amount", "cost", "total"],
};
const BOOKING_TEMPLATE = "client_name,client_phone,client_email,service,staff,date,time,status,price\nJane Smith,0400 000 000,jane@example.com,Hydrafacial,Ava,2026-09-01,14:00,booked,180\n";

function mapBookingRows(rows) {
  if (rows.length < 2) return [];
  const headers = rows[0].map(norm);
  const idx = {};
  for (const [key, aliases] of Object.entries(BOOKING_MAP)) {
    const at = headers.findIndex((h) => aliases.includes(h));
    if (at >= 0) idx[key] = at;
  }
  const get = (r, k) => (idx[k] != null ? (r[idx[k]] || "").trim() : "");
  return rows.slice(1).map((r) => ({
    client_name: get(r, "client_name"), client_phone: get(r, "client_phone"), client_email: get(r, "client_email"),
    service: get(r, "service"), staff: get(r, "staff"), date: get(r, "date"), time: get(r, "time"),
    status: get(r, "status"), price: get(r, "price") ? parseFloat(get(r, "price").replace(/[^0-9.]/g, "")) : null,
  })).filter((b) => b.service && b.staff && b.date);
}

const SERVICE_MAP = {
  name: ["name", "service", "servicename", "treatment", "treatmentname"],
  category: ["category", "categoryname", "group", "type"],
  duration_min: ["duration", "durationmin", "minutes", "mins", "length", "time"],
  buffer_min: ["buffer", "buffermin", "bufferminutes", "cleanup", "gap"],
  price: ["price", "cost", "amount", "rrp", "fee", "rate"],
};
const SERVICE_TEMPLATE = "name,category,duration_min,buffer_min,price\nUnderarm Laser,Laser,20,10,60\nDermal Filler 1ml,Injectables,45,0,550\n";

function mapServiceRows(rows) {
  if (rows.length < 2) return [];
  const headers = rows[0].map(norm);
  const idx = {};
  for (const [key, aliases] of Object.entries(SERVICE_MAP)) {
    const at = headers.findIndex((h) => aliases.includes(h));
    if (at >= 0) idx[key] = at;
  }
  const get = (r, k) => (idx[k] != null ? (r[idx[k]] || "").trim() : "");
  const num = (v) => { const n = parseFloat((v || "").replace(/[^0-9.]/g, "")); return isNaN(n) ? 0 : n; };
  return rows.slice(1).map((r) => ({
    name: get(r, "name"), category: get(r, "category"),
    duration_min: Math.round(num(get(r, "duration_min"))) || 30,
    buffer_min: Math.round(num(get(r, "buffer_min"))) || 0,
    price: num(get(r, "price")),
  })).filter((s) => s.name);
}

const CFG = {
  clients: { title: "Import clients from CSV", endpoint: "/clients/import", key: "clients", template: TEMPLATE, filename: "azordi-client-import-template.csv",
    blurb: "Upload a .csv with your clients. We auto-match columns like name, email, phone, date of birth, source, marketing opt-in and legacy. Existing clients (matched by phone or email) are skipped.",
    map: mapRows, preview: (c) => [c.name, c.phone || c.email || "—"], noun: "clients" },
  bookings: { title: "Import bookings from CSV", endpoint: "/bookings/import", key: "bookings", template: BOOKING_TEMPLATE, filename: "azordi-booking-import-template.csv",
    blurb: "Upload a .csv of appointments. We match each row to a client (by phone/email, or create by name), and to a service & staff member by name. Past dates fill history; future dates land on the calendar.",
    map: mapBookingRows, preview: (b) => [`${b.client_name || "Client"} · ${b.service}`, `${b.date} ${b.time || ""}`], noun: "bookings" },
  services: { title: "Import services from CSV", endpoint: "/services/import", key: "services", template: SERVICE_TEMPLATE, filename: "azordi-service-import-template.csv",
    blurb: "Upload a .csv of your services. We auto-match columns like name, category, duration, buffer and price. Categories are created automatically by name; services with a name that already exists are skipped.",
    map: mapServiceRows, preview: (s) => [`${s.name}${s.category ? " · " + s.category : ""}`, `${s.duration_min}min · $${s.price}`], noun: "services" },
};

export const ClientImport = ({ onDone, resource = "clients", trigger }) => {
  const cfg = CFG[resource];
  const [open, setOpen] = useState(false);
  const [parsed, setParsed] = useState([]);
  const [fileName, setFileName] = useState("");
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const inputRef = useRef(null);

  const reset = () => { setParsed([]); setFileName(""); setResult(null); if (inputRef.current) inputRef.current.value = ""; };

  const onFile = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setFileName(f.name); setResult(null);
    const reader = new FileReader();
    reader.onload = () => {
      try {
        const rows = parseCSV(String(reader.result || ""));
        const clients = cfg.map(rows);
        if (!clients.length) { toast.error(resource === "bookings" ? "No bookings found — need columns for service, staff and date." : "No clients found — make sure the first row has column headers incl. a name column."); setParsed([]); return; }
        setParsed(clients);
      } catch { toast.error("Couldn't read that file — is it a .csv?"); }
    };
    reader.readAsText(f);
  };

  const doImport = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(cfg.endpoint, { [cfg.key]: parsed });
      setResult(data);
      toast.success(`Imported ${data.imported} ${data.imported === 1 ? cfg.noun.slice(0, -1) : cfg.noun}`);
      onDone && onDone();
    } catch (e) { toast.error(e?.response?.data?.detail || "Import failed"); }
    finally { setBusy(false); }
  };

  const downloadTemplate = () => {
    const blob = new Blob([cfg.template], { type: "text/csv" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob); a.download = cfg.filename; a.click();
    URL.revokeObjectURL(a.href);
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (!o) reset(); }}>
      <DialogTrigger asChild>{trigger || <Button variant="outline" className="rounded-lg" data-testid="import-clients-button"><Upload className="h-4 w-4 mr-1.5" />Import</Button>}</DialogTrigger>
      <DialogContent data-testid="import-clients-dialog">
        <DialogHeader><DialogTitle className="font-heading">{cfg.title}</DialogTitle></DialogHeader>

        {result ? (
          <div className="py-4 text-center space-y-2" data-testid="import-result">
            <CheckCircle2 className="h-10 w-10 text-emerald-500 mx-auto" />
            <p className="font-heading font-semibold text-lg">Imported {result.imported} {cfg.noun}</p>
            <p className="text-sm text-muted-foreground">
              {result.duplicates > 0 && <>{result.duplicates} skipped as duplicates. </>}
              {result.skipped > 0 && <>{result.skipped} skipped. </>}
              from {result.total} rows.
            </p>
            {result.errors && result.errors.length > 0 && <p className="text-xs text-amber-600">{result.errors.join(" · ")}</p>}
            <Button className="rounded-lg mt-2" onClick={() => { setOpen(false); reset(); }} data-testid="import-done">Done</Button>
          </div>
        ) : (
          <>
            <div className="space-y-3 py-1">
              <p className="text-sm text-muted-foreground">{cfg.blurb}</p>
              <button onClick={downloadTemplate} className="text-sm text-primary inline-flex items-center gap-1.5 hover:underline" data-testid="download-template"><Download className="h-4 w-4" />Download the CSV template</button>

              <label className="flex flex-col items-center justify-center gap-2 rounded-xl border-2 border-dashed border-border p-6 cursor-pointer hover:border-primary/50 transition-colors" data-testid="import-dropzone">
                <FileSpreadsheet className="h-8 w-8 text-muted-foreground" />
                <span className="text-sm font-medium">{fileName || "Choose a CSV file"}</span>
                <span className="text-xs text-muted-foreground">Click to browse</span>
                <input ref={inputRef} type="file" accept=".csv,text/csv" className="hidden" onChange={onFile} data-testid="import-file-input" />
              </label>

              {parsed.length > 0 && (
                <div className="rounded-lg border border-border p-3" data-testid="import-preview">
                  <div className="text-sm font-medium mb-2">{parsed.length} {cfg.noun} ready to import</div>
                  <div className="max-h-40 overflow-y-auto text-xs divide-y divide-border/60">
                    {parsed.slice(0, 8).map((c, i) => {
                      const [a, b] = cfg.preview(c);
                      return <div key={i} className="py-1.5 flex justify-between gap-2"><span className="font-medium truncate">{a}</span><span className="text-muted-foreground truncate">{b}</span></div>;
                    })}
                    {parsed.length > 8 && <div className="py-1.5 text-muted-foreground">+{parsed.length - 8} more…</div>}
                  </div>
                </div>
              )}
            </div>
            <DialogFooter>
              <Button variant="outline" className="rounded-lg" onClick={() => { setOpen(false); reset(); }}>Cancel</Button>
              <Button className="rounded-lg" disabled={!parsed.length || busy} onClick={doImport} data-testid="import-confirm"><Upload className="h-4 w-4 mr-1.5" />{busy ? "Importing…" : `Import ${parsed.length || ""} ${cfg.noun}`}</Button>
            </DialogFooter>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};
